<template>
    <v-menu v-if="refundable" v-model="menu" :close-on-content-click="false" offset-x right top nudge-right="10">
        <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on"><v-icon>mdi-cash-refund</v-icon></v-btn>
        </template>

        <v-card style="min-width: 350px; padding: 20px">
            <span>Effectuer un remboursement</span>
            
            <!-- Afficher le moyen de paiement original -->
            <v-divider class="my-3"></v-divider>
            <div class="mb-2">
                <small class="grey--text">Paiement original</small>
                <div class="d-flex align-center mt-1">
                    <v-icon small class="mr-2">{{ getMethodIconById(item.payment_method_id) }}</v-icon>
                    <span>{{ getPaymentMethodName(item.payment_method_id) }}</span>
                </div>
                
                <!-- ✅ Afficher les montants -->
                <div class="mt-2">
                    <small class="grey--text">Montant payé : {{ item.amount }}€</small><br>
                    <small class="grey--text">Déjà remboursé : {{ totalRefunded }}€</small><br>
                    <small class="primary--text font-weight-bold">Peut être rembourser : {{ refundableAmount }} €</small>
                </div>
            </div>
            
            <!-- Méthode de remboursement -->
            <v-divider class="my-3"></v-divider>
            <small>Méthode de remboursement</small>
            <div class="mb-3 mt-2">
                <v-select
                    v-model="selectedMethod"
                    :items="availableRefundMethods"
                    item-text="name"
                    item-value="id"
                    label=""
                    dense
                    outlined
                    :rules="[rules.required]"
                    prepend-inner-icon="mdi-cash-refund"
                    :disabled="availableRefundMethods.length === 1"
                >
                    <template v-slot:selection="{ item }">
                        {{ item.name }}
                    </template>
                    <template v-slot:item="{ item }">
                        {{ item.name }}
                    </template>
                </v-select>
                
                <!-- Message pour remboursement identique obligatoire -->
                <div v-if="item.payment_method_id < 5" class="mt-2">
                    <v-alert type="info" dense text>
                        Le remboursement doit être effectué par le même moyen de paiement
                    </v-alert>
                </div>
            </div>
            
            <v-divider></v-divider>

            <span v-if="error" style="color: red">{{ error }}</span>
            <v-list>
                <div style="width: 100%">
                    <v-form v-model="valid">
                        <v-text-field
                            single-line
                            dense
                            label="Montant"
                            v-model="amount"
                            :rules="[
                                rules.required,
                                rules.float,
                                value => parseFloat(value) <= parseFloat(refundableAmount) || `Maximum ${refundableAmount}€`,
                                validateRefundAmount
                            ]"
                            type="number"
                            step="any"
                            suffix="€"
                            style="max-width: 200px"
                            :max="refundableAmount"
                        ></v-text-field>
                        <!-- ✅ Encart pour la note -->
                        <v-textarea
                            v-model="note"
                            label=""
                            placeholder="Raison du remboursement, commentaires..."
                            dense
                            outlined
                            rows="3"
                            counter="500"
                            :rules="[
                                value => !value || value.length <= 500 || 'Maximum 500 caractères'
                            ]"
                            class="mt-3"
                        >
                            <template v-slot:prepend-inner>
                                <v-icon small class="mt-1">mdi-note-text-outline</v-icon>
                            </template>
                        </v-textarea>
                    </v-form>
                    
                    <!-- Message d'avertissement pour remboursement complet -->
                    <div v-if="requiresFullRefund && parseFloat(amount) !== parseFloat(item.amount)" class="mt-2">
                        <v-alert type="warning" dense text>
                            Ce mode de paiement nécessite un remboursement complet ({{ refundableAmount }}€)  
                            <!-- ✅ Changé -->
                        </v-alert>
                    </div>
                </div>
            </v-list>
            <div></div>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="menu = false">
                    Annuler
                </v-btn>
                <v-btn 
                    color="primary" 
                    text 
                    @click="refund" 
                    :disabled="!valid || !selectedMethod" 
                    :loading="loading"
                >
                    Rembourser
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-menu>
</template>

<script>
import axios from 'axios'

export default {
    props: [
        'userId', 
        'orderId', 
        'item', 
        'refundable', 
        'refundableAmount'
    ],
    
    mounted() {
        console.log('=== PROPS DEBUG ===')
        console.log('item:', this.item)
        console.log('refundable:', this.refundable)
        console.log('refundableAmount:', this.refundableAmount)
        
        // ✅ Initialiser avec le montant remboursable au lieu de item.amount
        this.amount = parseFloat(this.refundableAmount)
        this.getPaiementMethod()
    },
    
    data() {
        return {
            amount: 0,
            menu: false,
            valid: false,
            loading: false,
            error: null,
            selectedMethod: null,
            methods: [],
            note:'',
            rules: {
                required: value => !!value || 'Element requis.',
                number: value => {
                    const pattern = /^[0-9]*$/
                    return pattern.test(value) || 'Format incorrect'
                },
                float: value => {
                    const pattern = /^[0-9]+(\.[0-9]+)?$/
                    return pattern.test(value) || 'Format incorrect'
                }
            },
            
            // Mapping des règles de remboursement (pour id >= 5)
            refundRules: {
                'stripe': ['rbt avoir', 'espèces', 'stripe'],
                'bon cadeau': ['bon cadeau', 'rbt avoir'],
                'espèces': ['espèces', 'rbt avoir'],
                'rbt avoir': ['espèces', 'rbt avoir'],
                'cb': ['espèces', 'rbt avoir'],
                'virement': ['espèces', 'rbt avoir'],
                'cure': ['cure'],
                'serie': ['serie'],
                'points de fidélité': ['points de fidélité'],
                'chèque': ['chèque', 'espèces', 'rbt avoir' ]
            },
            
            // Types qui nécessitent un remboursement complet
            fullRefundRequired: ['stripe', 'cure', 'serie', 'point de fidélité']
        }
    },
    
    computed: {

        // ✅ Calculer le montant déjà remboursé (pour affichage uniquement)
        totalRefunded() {
            // console.log('this.refundableAmount',this.refundableAmount);
            return Math.max(0, parseFloat(this.item.amount) - parseFloat(this.refundableAmount))
        },
        
        // Calculer le montant restant à rembourser
        remainingAmount() {
            const paid = parseFloat(this.item.amount || 0)
            const refunded = this.totalRefunded
            return Math.max(0, paid - refunded)
        },
        
        // Vérifier si on peut encore rembourser
        canRefund() {
            return this.remainingAmount > 0
        },
        // Récupérer l'objet de la méthode de paiement originale
        originalPaymentMethod() {
            if (!this.item || !this.methods.length) {
                return null
            }
            return this.methods.find(m => m.id === this.item.payment_method_id)
        },
        
        // Filtrer les méthodes de remboursement disponibles
        availableRefundMethods() {
            if (!this.item || !this.methods.length) {
                return []
            }
            
            // CAS 1 : Si payment_method_id < 5 → remboursement UNIQUEMENT par le même moyen
            if (this.item.payment_method_id < 5) {
                return this.methods.filter(method => method.id === this.item.payment_method_id)
            }
            
            // CAS 2 : Si payment_method_id >= 5 → appliquer les règles de remboursement
            const originalMethod = this.originalPaymentMethod
            if (!originalMethod || !originalMethod.name) {
                // Si pas de type spécifié, afficher tous les modes >= 5
                return this.methods.filter(method => method.id >= 5)
            }
            
            const originalPaymentType = originalMethod.name.toLowerCase()
            const allowedTypes = this.refundRules[originalPaymentType] || []
            
            // Filtrer les méthodes selon les règles ET exclure les id < 5
            return this.methods.filter(method => {
                return allowedTypes.includes(method.name.toLowerCase())
            })
        },
        
        // Vérifier si le remboursement doit être complet
        requiresFullRefund() {
            if (!this.item) {
                return false
            }
            
            // Si payment_method_id < 5, remboursement complet obligatoire
            if (this.item.payment_method_id < 5) {
                return true
            }
            
            // Sinon, vérifier selon le type
            const originalMethod = this.originalPaymentMethod
            if (!originalMethod || !originalMethod.type) {
                return false
            }
            
            const originalPaymentType = originalMethod.type.toLowerCase()
            return this.fullRefundRequired.includes(originalPaymentType)
        }
    },
    
    methods: {
        getMethodIcon(type) {
            if (!type) return 'mdi-cash-multiple'
            
            const icons = {
                'wallet': 'mdi-wallet',
                'avoir_wallet': 'mdi-wallet',
                'avoir': 'mdi-ticket-percent',
                'bon_cadeau': 'mdi-gift',
                'stripe': 'mdi-credit-card',
                'cb': 'mdi-credit-card',
                'especes': 'mdi-cash',
                'cash': 'mdi-cash',
                'virement': 'mdi-bank-transfer',
                'cure': 'mdi-spa',
                'serie': 'mdi-package-variant',
                'point_fidelite': 'mdi-star-circle'
            }
            return icons[type.toLowerCase()] || 'mdi-cash-multiple'
        },
        
        // Récupérer l'icône par ID
        getMethodIconById(methodId) {
            const method = this.methods.find(m => m.id === methodId)
            return method ? this.getMethodIcon(method.type) : 'mdi-cash-multiple'
        },
        
        // Récupérer le nom de la méthode de paiement par ID
        getPaymentMethodName(methodId) {
            if (!methodId || !this.methods.length) return 'Non spécifié'
            
            const method = this.methods.find(m => m.id === methodId)
            return method ? method.name : 'Non spécifié'
        },
        
        // Validation du montant pour les remboursements complets
        validateRefundAmount(value) {
            if (!this.requiresFullRefund) {
                return true
            }
            
            const amount = parseFloat(value)
            const requiredAmount = parseFloat(this.refundableAmount)  // ✅ Changé
    
            if (amount !== requiredAmount) {
                return `Le remboursement doit être complet (${requiredAmount}€)`  // ✅ Changé
            }
            
            return true
        },
        
        insertAfterEveryN(str, char, n, res) {
            const result = str.split('').reduce((accumulator, current, index) => {
                if (index % n === res) {
                    return accumulator + current + char
                }
                return accumulator + current
            }, '')
            return result
        },
        
        async refund() {
            console.log('id', this.item.id)
            console.log('amount', this.amount)
            console.log('method', this.selectedMethod)
            console.log('original_payment_method_id', this.item.payment_method_id)
            console.log('note', this.note)
            
            const originalMethod = this.originalPaymentMethod
            // console.log('original_payment_method_type', originalMethod ? originalMethod.name : null)
            
            // this.loading = true
            this.error = null
            
            axios
                .post('/back-office/caisse/refoundOrderInvoice', { 
                    id: this.item.id, 
                    amount: this.amount, 
                    method_id: this.selectedMethod,
                    original_payment_method_id: this.item.payment_method_id,
                    note: this.note,
                })
                .then((res) => {
                    console.log('res',res);
                    // window.location.reload()
                })
                .catch(error => {
                    this.error = error?.response?.data?.message || 'Une erreur est survenue'
                })
                .finally(() => {
                    this.loading = false
                })
        },

        async getPaiementMethod() {
            this.loading = true
            this.error = null
            
            axios
                .get('/back-office/paiement-method/list')
                .then((res) => {
                    this.methods = res.data || []
                    
                    // Présélectionner la première méthode disponible
                    if (this.availableRefundMethods.length > 0) {
                        this.selectedMethod = this.availableRefundMethods[0].id
                    }
                })
                .catch(error => {
                    this.error = error?.response?.data?.message || 'Une erreur est survenue'
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
}
</script>